import { calculateArrayHashCode } from "src/util/arrays";
import { getEchartOptionByChartType } from "./strategies/echartOptionResolver";
import { ChartManagerInterface } from "./interface";
import { useLegendHandler } from "./stateManagement/legendHandler";
import { getChartManagerInstance } from "./stateManagement/management";
import ColorPickerManager from "./colorPicker";

/**
 * Classe gerenciadora principal: Tem a responsabilidade de gerenciar
 * responsabilidades comuns entre os gráficos do analyze, tais como:
 *
 * - Atualizar as opções de um gráfico.
 * - Atualizar o estado das legendas visíveis no gráfico.
 * - Recuperar informações úteis do gráfico, como suas dimensões, suas legendas, o tipo do gráfico, etc.
 */
export class ChartManager extends ChartManagerInterface {
  // Instância da classe de um gráfico (BarChart | PieChart | FunnelChart | ChartN)
  constructor(instance) {
    super();
    this.instance = instance;
    // Gerenciador do estado das legendas do gráfico
    this.legendHandler = useLegendHandler(this);
    // Gerenciador do color picker
    this.colorPickerHandler = new ColorPickerManager(this);
    // Atualiza as propriedades iniciais do gráfico
    this._init();
  }

  // Atualiza as propriedades do gráfico com base nos dados de entrada (dados, cores e fontes).
  _init() {
    this._setProps();
    this._setUpdateOnlyState();
    this._handleEchartsUpdates();
  }

  // Evento: Disparado no evento "legendselectchanged" do gráfico.
  handleOnLegendChange(eventContext) {
    this.legendHandler.update(eventContext);
    // Callback: Caso a classe que extende de ChartManager possua um evento que deve ser executado
    // na action de habilitar - desabilitar uma legenda ela será chamada aqui.
    this.getInstances(true).forEach(m => m._handleEchartsUpdates(true))
  }

  // Evento: Disparado no evento "dblclick" do gráfico.
  handleOnDoubleClick(eventContext) {
    this.colorPickerHandler.openColorPicker(eventContext);
  }

  /**
   * Resolve a atualização do gráfico com base nas ultimas propriedades do gráfico (data, colors, legends, fontsizes).
   */
  _handleEchartsUpdates(shouldUpdateOptions = false) {
    const identifier = this.getIdentifier();
    const { data, chartFontSize, colors, highlitIndex, chartSize } = this.getProps();
    const { ascOrder } = this.getComponentProps();
    const currentOptions = this.getOptions();
    const disabledLegends = this.getDisabledLegends();

    const updateOnlyStrategy = this.getUpdateOnly();

    // Preparo de opções que serão consumidas pelo echartOption do gráfico.
    const echartOptionParams = {
      data: !updateOnlyStrategy ? this.formatData(data) : null,
      colors,
      chartFontSize,
      chartSize: chartSize || this.getChartSizes(),
      disabledLegends,
      currentOptions,
      highlitIndex,
      ascOrder
    };
    
    // Resolve e recupera as novas opções do gráfico com base no echartOption resolvido pelo método auxiliar
    const { options, toEchartOption, optionsToEcharts } = getEchartOptionByChartType(
      identifier,
      echartOptionParams,
      updateOnlyStrategy,
    );

    this._resetUpdateOnly();

    this.options = options;

    if (toEchartOption)
      this.toEchartOption = toEchartOption;

    // Atualiza o gráfico utilizando a api do echarts.
    if (shouldUpdateOptions) {
      this.updateOptions(optionsToEcharts || this.options);
    }
  }

  /**
   * Interceptador de mudanças no componente do gráfico instanciado, tem o objetivo de interceptar quais propriedades
   * do gráfico mudaram, sejam seus dados plotados, as cores ou as fontes, e atualizar o gráfico de acordo com essas
   * mudanças.
   *
   * IMPORTANTE: Esse método sempre deve ser chamado no método de ciclo de vida shouldComponentUpdate() da classe
   * do gráfico.
   */
  changesChecker(nextProps) {
    const { data, colors, chartFontSize, ascOrder: currentAscOrder } = this.getComponentProps();
    const {
      data: nextData,
      colors: nextColors,
      chartFontSize: nextFontSize,
      ascOrder: nextAscOrder
    } = nextProps;

    // Verifica se os dados sofreram alteração
    const dataChanged =
      calculateArrayHashCode(data) !== calculateArrayHashCode(nextData);
    // Verifica se as fontes sofreram alteração
    const fontChanged = chartFontSize !== nextFontSize;
    // Verifica se as cores sofreram alteração
    const colorsChanged = colors.toString() !== nextColors.toString();

    const ascOrderChanged = nextAscOrder !== currentAscOrder;
    
    const somePropertyChanged =
      dataChanged || fontChanged || colorsChanged || ascOrderChanged;
  
    this.setUpdateOnly({ fontSize: fontChanged, colors: colorsChanged });

    if (somePropertyChanged) {
      this.updateProps(nextData, nextColors, nextFontSize, nextAscOrder);

      // Aplica as alterações no gráfico
      this._handleEchartsUpdates();
    }

    // ComponentShouldUpdate deve retornar "true" ou "false", só retornará true caso alguma propriedade tenha sofrido alteração.
    return somePropertyChanged;
  }

  /**
   * Atualiza as propriedades do gráfico. "chartProps" é um objeto que sempre manterá as propriedades relevantes
   * para o gráfico em seu ultimo estado, e será consumido ao atualizar as opções do gráfico.
   * @param {array} data
   * @param {array} colors
   * @param {number} chartFontSize
   */
  updateProps(data, colors, chartFontSize, highlitIndex, chartSizes, ascOrder) {
    if (Array.isArray(data)) this.chartProps.data = data;
    if (Array.isArray(colors)) this.chartProps.colors = { ...this.instance.props.theme, colors };
    if (typeof chartFontSize === 'number') this.chartProps.chartFontSize = chartFontSize;
    if (typeof highlitIndex === 'number') this.chartProps.highlitIndex = highlitIndex;
    if (chartSizes) this.chartProps.chartSize = chartSizes;
    if (typeof ascOrder === 'boolean') this.chartProps.ascOrder = ascOrder;
  }

  /**
   * Atualiza as opções do gráfico através do método setOption(newOptions) disponibilizado pela lib echarts.
   * @param {object} options
   */
  updateOptions(options) {
    if (this.instance && this.instance.echarts) {
      this.instance.echarts.setOption(options);
    }
  }

  /**
   * Setters
   */

  /**
   * Atualiza as propriedades no formato em que os gráficos o consomem.
   */
  _setProps() {
    const { data, chartFontSize, colors, theme , ascOrder} = this.getComponentProps();

    this.chartProps = {
      data: data,
      chartFontSize,
      colors: {
        ...theme,
        colors,
      },
      highlitIndex: -1,
      chartSize: null,
      ascOrder: ascOrder
    };
  }

  /**
   * Armazena um estado inicial das variáveis de atualização única
   * 
   */
  _setUpdateOnlyState() {
    this.updateOnly = {
      fontSize: false,
      colors: false,
      highlitIndex: false,
      resizing: false,
    }
  }

  /**
   * Reseta as variáveis de atualização única.
   */
  _resetUpdateOnly() {
    this.updateOnly.fontSize = false;
    this.updateOnly.colors = false;
    this.updateOnly.highlitIndex = false;
    this.updateOnly.resizing = false;
  }

  /**
   * Atualiza a propriedade que sofreu alteração no gráfico, podendo ser
   * o tamanho das fontes, suas cores ou o item do gráfico clicado.
   * @param {object} options Opções de atualização única { fontSize, colors, highlitIndex } 
   */
  setUpdateOnly(options = {}) {
    this._resetUpdateOnly();

    const keys = Object.keys(options);
    const updates = keys.filter(key => options[key]);

    if (!updates.length || updates.length > 1) return;

    this.updateOnly[updates[0]] = true;
  }

  /**
   * Getters
   */
  getProps() {
    return this.chartProps;
  }

  /**
   * Recupera um identificador para uma propriedade única
   * que sofreu alteração, podendo ser { colors | fontSize | highlitIndex }
   * caso uma dessas propriedades tenha sofrido alteração o seu identificador
   * será retornado e utilizado posteriormente na strategy de atualização
   * única do echarts para meios de eficiência.
   */
  getUpdateOnly() {
    let property;
    for (property in this.updateOnly) {
      if (this.updateOnly[property]) {
        return property;
      }
    }

    return undefined;
  }

  getComponentProps() {
    const { data, chartFontSize, colors, theme, ascOrder } =
      this.instance.props;

    return {
      data,
      chartFontSize,
      colors,
      theme,
      ascOrder
    };
  }

  getOptions() {
    return this.options;
  }

  getChartType() {
    return this.instance.props.chartType;
  }

  getCardType() {
    return this.instance.props.cardType;
  }

  /**
   * Retorna um identificador para o gráfico, que é a junção entre o nome do card relacionado ao gráfico e o tipo do gráfico
   * atualmente renderizado.
   * @returns identificador do gráfico
   */
  getIdentifier() {
    const [cardType, chartType] = [this.getCardType(), this.getChartType()];

    return `${cardType}_${chartType}`;
  }

  getEcharts() {
    return this.instance && this.instance.echarts;
  }

  /**
   * Recupera as instâncias dos gerentes ativos dado um card. Isto é necessário
   * pois alguns cards podem renderizar mais de um gráfico ao mesmo tempo, e com isso,
   * temos um número N > 1 de instâncias de gerentes, como é o caso do card de cruzamentos simples.
   * Em alguns contextos precisamos de uma referência para todos esses gerentes para fazer alterações em todos
   * esses gráficos ao mesmo tempo, como atualizar suas cores ou aplicar o efeito de highlit quando acionamos
   * o color picker, por exemplo.
   * @param {boolean} asArray true para retornar as instâncias dos gerenciadores em formato de array 
   * @returns {Array<ChartManager>}
   */
  getInstances(asArray = false) {
    const card = this.getCardType();
    const managers = getChartManagerInstance(card);
    const isArrayOfManagers = Array.isArray(managers);

    if (asArray && !isArrayOfManagers) {
      return [managers];
    }
    
    if (isArrayOfManagers) {
      return managers;
    }

    return asArray ? [managers] : managers;
  }

  /**
   * Retorna as dimensões do gráfico no formato object<{ width, height }>
   * @returns {object | null}
   */
  getChartSizes() {
    const echarts = this.getEcharts();

    if (echarts) {
      return {
        width: echarts.getWidth(),
        height: echarts.getHeight(),
      };
    }

    return this.chartProps.chartSize || null;
  }

  getDisabledLegends() {
    return [...this.legendHandler.state];
  }

  getQuestionType() {
    return (this.instance && this.instance.props.tipoPergunta) || null;
  }

  isMultipleQuestionType() {
    return this.getQuestionType() === "MULTIPLA";
  }

  isUniqueGradeQuestionType() {
    return this.getQuestionType() === "GRADE_UNICA";
  }

  isMultiGradeQuestionType() {
    return this.getQuestionType() === "GRADE_MULTIPLA";
  }
}
